/**
 * Centralized Firestore collection name constants.
 *
 * This is the single source of truth for every collection name used by
 * both `apps/web` and `functions`. Domain-specific alias objects
 * (e.g. Sales WORKFLOW_COLLECTIONS) should re-export from here.
 */
export const COLLECTION_NAMES = {
    // ── User / System ──────────────────────────────────────────────
    APP_USERS: 'system_users',
    USER_GROUPS: 'user_groups',
    USER_PIVOT_SYNC: 'user_pivot_sync',
    ACTIVITY_LOG: 'activity_log',
    AUDIT_LOGS: 'audit_logs',
    SYNC_LOGS: 'sync_logs',
    COMMENTS: 'comments',
    // ── Billing / Workflow Core ────────────────────────────────────
    COLLECTION_CYCLES: 'collection_cycles',
    WORKFLOW_ASSIGNMENTS: 'workflow_assignments',
    DATA_SUBMISSIONS: 'data_submissions',
    ARCHIVED_DATA_SUBMISSIONS: 'archived_data_submissions',
    PROGRAM_CONFIGS: 'program_configs',
    WORKFLOW_CONFIGS: 'workflow_configs',
    WORKFLOWS: 'workflows',
    SETUP_PROGRESS: 'setup_progress',
    WORKFLOW_DIVISIONS: 'workflow_divisions',
    PROGRAM_ASSIGNMENTS: 'program_assignments',
    MANUAL_PROGRAMS: 'manual_programs',
    // ── Team Workflow (three-collection design) ────────────────────
    WORKFLOW_TEAM_PROGRAMS: 'workflow_team_programs',
    WORKFLOW_TEAM_TEAMS: 'workflow_team_teams',
    WORKFLOW_TEAM_WORKFLOWS: 'workflow_team_workflows',
    WORKFLOW_SPOTS: 'workflow_spots',
    // ── Workflow Summary ───────────────────────────────────────────
    WORKFLOW_SUMMARY: 'workflow_summary',
    WORKFLOW_SUMMARY_WORKFLOWS: 'workflow_summary_workflows',
    WORKFLOW_SUMMARY_DIVISIONS: 'workflow_summary_divisions',
    // ── BQ-synced read cache (bigquery_*) ──────────────────────────
    BIGQUERY_CURRENCIES: 'bigquery_currencies',
    BIGQUERY_DEALS: 'bigquery_deals',
    BIGQUERY_SPEND: 'bigquery_spend',
    BIGQUERY_WORKFLOWS: 'bigquery_workflows',
    BIGQUERY_EMPLOYEE_DATA: 'bigquery_employee_data',
    BIGQUERY_WORKFLOW_RUN_RATE: 'bigquery_workflow_run_rate',
    BIGQUERY_DEAL_TIERS: 'bigquery_deal_tiers',
    BIGQUERY_MONTHLY_DEAL_DATA: 'bigquery_monthly_deal_data',
    BIGQUERY_MONTHLY_AGGREGATED_DEAL_DATA: 'bigquery_monthly_aggregated_deal_data',
    BIGQUERY_MONTHLY_DEAL_STAGES: 'bigquery_monthly_deal_stages',
    BIGQUERY_MONTHLY_LEAD_SOURCE: 'bigquery_monthly_lead_source',
    BIGQUERY_MONTHLY_CONVERSION_DATA: 'bigquery_monthly_conversion_data',
    BIGQUERY_MONTHLY_FIRMOGRAPHIC_DATA: 'bigquery_monthly_firmographic_data',
    BIGQUERY_CRM_ORGANIZATIONS: 'bigquery_crm_organizations',
    BIGQUERY_BUSINESS_OPS: 'bigquery_business_ops',
    // ── Intel (intel_*) ────────────────────────────────────────────
    INTEL_TOP_LEVEL: 'intel_top_level',
    INTEL_CORE_WORKFLOW: 'intel_core_workflow',
    INTEL_WORKFLOW_REPORT: 'intel_workflow_report',
    INTEL_EMPLOYEE_REPORT: 'intel_employee_report',
    INTEL_CORE_WORKFLOW_PEOPLE: 'intel_core_workflow_people',
    INTEL_CORE_TEAM_MEMBERS: 'intel_core_team_members',
    INTEL_FTE_CATEGORIES: 'intel_fte_categories',
    // ── CRM (crm_*) ───────────────────────────────────────────────
    CRM_COMPANIES: 'crm_companies',
    CRM_PEOPLE: 'crm_people',
    CRM_MOMENTS: 'crm_moments',
    CRM_REVIEWS: 'crm_reviews',
    SPEND_FORECASTS: 'spend_forecasts',
    // ── Sales (sales_workflow_*) ───────────────────────────────────
    SALES_WORKFLOW_DEALS: 'sales_workflow_deals',
    SALES_WORKFLOW_AUDITORS: 'sales_workflow_auditors',
    SALES_WORKFLOW_SETTINGS: 'sales_workflow_settings',
    SALES_WORKFLOW_AUDIT_RECORDS: 'sales_workflow_audit_records',
    // ── Hiring ─────────────────────────────────────────────────────
    HIRING_SPOTS: 'hiring_spots',
    HIRING_FORECASTS: 'hiring_forecasts',
    HIRING_DYNAMICS: 'hiring_dynamics',
    HIRING_FORECASTS_CALCULATED: 'hiring_forecasts_calculated',
    HIRING_CONFIRMED_HIRES: 'hiring_confirmed_hires',
    HIRING_ROLES: 'hiring_roles',
    HIRING_RATE_CARD: 'hiring_rate_card',
    HIRING_POSITIONS: 'hiring_positions',
    HEADCOUNT_ATTRIBUTION_SNAPSHOTS: 'headcount_attribution_snapshots',
    HIRING_ENVELOPES: 'hiring_envelopes',
    HIRING_DEPARTURE_CREDITS: 'hiring_departure_credits',
    HIRING_POSITION_EXCEPTIONS: 'hiring_position_exceptions',
    GLOBAL_CALENDAR_EVENTS: 'global_calendar_events',
    CALENDAR_EVENT_TEMPLATES: 'calendar_event_templates',
    MINOR_FLAGS: 'minor_flags',
    // ── Goals ──────────────────────────────────────────────────────
    GOALS_RUN_CARDS: 'goals_run_cards',
    GOALS_RUN_SETTINGS: 'goals_run_settings',
    GOALS_TRANSFORM_INITIATIVES: 'goals_transform_initiatives',
    GOALS_GROW_PORTFOLIOS: 'goals_grow_portfolios',
    // -- Creative
    CREATIVE_WORKFLOWS: 'workflow_creative',
    // ── Forecast ───────────────────────────────────────────────────
    FORECAST_SETTINGS: 'forecast',
    // ── Admin / Tools ──────────────────────────────────────────────
    ASANA_CONFIG: 'asana_config',
    ADMIN_GOOGLE_SHEETS: 'admin_google_sheets',
    SLACK_MESSAGE_TEMPLATES: 'slack_message_templates',
    CHART_BUILDER_CONNECTIONS: 'chart_builder_connections',
    CHART_BUILDER_CONFIGS: 'chart_builder_configs',
    // ── Cloud Functions only ───────────────────────────────────────
    BULK_OPERATION_PROGRESS: 'bulk_operation_progress',
    BULK_OPERATION_LOGS: 'bulk_operation_logs',
    FUNCTION_RUN_LOGS: 'function_run_logs',
    FUNCTION_LATEST_RUNS: 'function_latest_runs',
};
